import React, { useState, useEffect, useContext } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import RefreshIcon from "@material-ui/icons/Refresh";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { socketConnection } from "../../services/socket";
import Board from "react-trello";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },

  header: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1),
  },

  boardWrapper: {
    flex: 1,
    overflow: "hidden",
    position: "relative",
  },

  loadingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(0,0,0,0.5)"
      : "rgba(255,255,255,0.7)",
    zIndex: 10,
  },

  // Card customizado — tamanho fixo estilo card
  card: {
    backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#ffffff",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 8,
    cursor: "grab",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    "&:hover": {
      boxShadow: "0 3px 8px rgba(0,0,0,0.14)",
    },
    transition: "box-shadow 0.15s ease",
    width: 240,
    minWidth: 240,
    maxWidth: 240,
    boxSizing: "border-box",
    overflow: "hidden",
  },

  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },

  cardName: {
    fontWeight: 600,
    fontSize: "0.82rem",
    color: theme.palette.text.primary,
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  cardMessage: {
    fontSize: "0.73rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
    marginBottom: 8,
    lineHeight: "1.4",
    maxHeight: "2.8em",
  },

  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 6,
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: 6,
  },

  ticketChip: {
    height: 18,
    fontSize: "0.65rem",
    borderRadius: 6,
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.08)"
      : "rgba(0,0,0,0.06)",
    color: theme.palette.text.secondary,
  },

  openIcon: {
    padding: 2,
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
}));

// ---------------------------------------------------------------------------
// Custom Card Component
// ---------------------------------------------------------------------------
const KanbanCard = ({ id, title, description, label, onClick, classes, history }) => {
  const { message, uuid, number } = description || {};
  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <Typography className={classes.cardName} title={title}>
          {title}
        </Typography>
        <Tooltip title="Abrir ticket">
          <IconButton
            className={classes.openIcon}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              history.push(`/tickets/${uuid}`);
            }}
          >
            <OpenInNewIcon style={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </div>
      {number && (
        <Typography className={classes.cardMessage}>
          {number}
        </Typography>
      )}
      {message && (
        <Typography className={classes.cardMessage}>
          {message}
        </Typography>
      )}
      <div className={classes.cardFooter}>
        <Chip label={`#${id}`} size="small" className={classes.ticketChip} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Kanban Page
// ---------------------------------------------------------------------------
const Kanban = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const queueIds = user.queues.map((q) => q.UserQueue.queueId);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tagsRes, ticketsRes] = await Promise.all([
        api.get("/tags/kanban"),
        api.get("/ticket/kanban", {
          params: { queueIds: JSON.stringify(queueIds), teste: true },
        }),
      ]);
      setTags(tagsRes.data.lista || []);
      setTickets(ticketsRes.data.tickets || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Socket — atualiza em tempo real quando ticket é alterado
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-ticket`, (data) => {
      if (["update", "create", "delete"].includes(data.action)) {
        fetchData();
      }
    });

    return () => socket.disconnect();
  }, []);

  // Monta as raias do board
  const buildBoard = () => {
    // ID único por raia: "ticketId:laneId" — evita conflito quando o mesmo
    // ticket aparece em múltiplas raias
    const makeCards = (list, laneId) =>
      list.map((ticket) => ({
        id: `${ticket.id}:${laneId}`,
        title: ticket.contact.name,
        label: `#${ticket.id}`,
        description: {
          message: ticket.lastMessage,
          uuid: ticket.uuid,
          number: ticket.contact.number,
        },
        draggable: true,
      }));

    // Normaliza para Number para evitar mismatch string/number no Set
    const kanbanTagIds = new Set(tags.map((t) => Number(t.id)));

    // Cada ticket pertence a exatamente uma raia: a da primeira tag kanban encontrada
    const getTicketLane = (ticket) => {
      for (const tag of (ticket.tags || [])) {
        if (kanbanTagIds.has(Number(tag.id))) return String(tag.id);
      }
      return "open";
    };

    const openTickets = tickets.filter((t) => getTicketLane(t) === "open");

    const lanes = [
      {
        id: "open",
        title: "Em aberto",
        label: String(openTickets.length),
        style: {
          backgroundColor: theme.palette.type === "dark" ? "#1e1e1e" : "#f0f2f5",
          borderRadius: 10,
        },
        titleStyle: { color: theme.palette.text.primary, fontWeight: 700 },
        cards: makeCards(openTickets, "open"),
      },
      ...tags.map((tag) => {
        const laneId = tag.id.toString();
        const tagTickets = tickets.filter((t) => getTicketLane(t) === laneId);
        return {
          id: laneId,
          title: tag.name,
          label: String(tagTickets.length),
          style: {
            backgroundColor: tag.color ? `${tag.color}22` : "#f0f2f5",
            borderRadius: 10,
            borderTop: `3px solid ${tag.color || "#ccc"}`,
          },
          titleStyle: { color: tag.color || theme.palette.text.primary, fontWeight: 700 },
          cards: makeCards(tagTickets, laneId),
        };
      }),
    ];

    return { lanes };
  };

  const handleCardMove = async (fromLaneId, toLaneId, cardId) => {
    const ticketId = cardId.split(":")[0];
    try {
      if (fromLaneId !== "open") {
        await api.delete(`/ticket-tags/${ticketId}`);
      }
      if (toLaneId !== "open") {
        await api.put(`/ticket-tags/${ticketId}/${toLaneId}`);
      }
      fetchData();
    } catch (err) {
      toast.error("Erro ao mover ticket.");
      fetchData();
    }
  };

  const boardData = buildBoard();

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <Typography variant="h6" style={{ fontWeight: 700, flex: 1 }}>
          Kanban
        </Typography>
        <Tooltip
          title={
            <span>
              As raias são formadas pelas <strong>tags marcadas como Kanban</strong> nas configurações.
              <br /><br />
              Cada ticket aparece em <strong>apenas uma raia</strong> — a da primeira tag Kanban encontrada.
              Ao arrastar para outra raia, a tag anterior é removida automaticamente.
              <br /><br />
              Tickets exibidos são filtrados pelas <strong>filas do seu usuário</strong>.
            </span>
          }
          placement="bottom-end"
          arrow
        >
          <IconButton size="small" style={{ color: "inherit", opacity: 0.5 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Atualizar board">
          <IconButton size="small" onClick={fetchData} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {/* Board */}
      <div className={classes.boardWrapper}>
        {loading && (
          <div className={classes.loadingOverlay}>
            <CircularProgress size={36} />
          </div>
        )}
        <Board
          data={boardData}
          onCardMoveAcrossLanes={handleCardMove}
          hideCardDeleteIcon
          style={{
            backgroundColor: "transparent",
            height: "100%",
            padding: "12px 8px",
          }}
          components={{
            Card: (props) => (
              <KanbanCard
                {...props}
                classes={classes}
                history={history}
              />
            ),
          }}
        />
      </div>
    </div>
  );
};

export default Kanban;
